import { useEffect, useState, useCallback } from "react";
import classNames from "classnames";
import { useRouter } from "next/router";

import {
  BatchSendEmailFormValues,
  GetAnalyticsGroupMembersResponse,
  SHARED_ADMIN_ROUTES,
  SortOrder,
  getStudentStatusTagType,
  translate,
  useDebounce,
} from "@ascendnow/shared";
import styles from "./AscAnalyticsGroupMembersListTable.module.scss";

import { useAscContext } from "../../context";
import AscButton from "../AscButton/AscButton";
import AscTag from "../AscTag/AscTag";
import AscSearchInput from "../AscInputFields/AscSearchInput/AscSearchInput";
import AscBatchSendEmail from "../AscBatchSendEmail/AscBatchSendEmail";
import AscModalDelete from "../AscModalDelete/AscModalDelete";
import AscLoading from "../AscLoading/AscLoading";
import AscTableResponsive from "../AscTableResponsive/AscTableResponsive";
import { InfoCircleSolid } from "../AscIcons";

type AnalyticsGroupMemberTableItem = Pick<
  GetAnalyticsGroupMembersResponse[number],
  | "email"
  | "id"
  | "groupNames"
  | "name"
  | "school"
  | "status"
  | "member"
  | "groupIds"
>;

export interface AscAnalyticsGroupMembersListTableProps {
  className?: string;
  groupId: string;
  onGetEventMembers: (
    groupId: string,
    filters?: {
      sortBy?: string;
      sortDirection?: string;
      take?: number;
      skip?: number;
      searchTerm?: string;
    }
  ) => Promise<AnalyticsGroupMemberTableItem[]>;
  onSendEmail: (values: BatchSendEmailFormValues) => Promise<void>;
  onRemove: (studentId: string) => Promise<void>;
  onSave: (
    studentIds: string[],
    filters?: {
      sort?: { field: string; direction: SortOrder };
      page?: number;
      searchTerm?: string;
    }
  ) => Promise<void>;
}

const DEFAULT_PER_PAGE = 50;

export default function AscAnalyticsGroupMembersListTable({
  className,
  groupId,
  onGetEventMembers,
  onSendEmail,
  onRemove,
  onSave,
}: AscAnalyticsGroupMembersListTableProps) {
  const router = useRouter();
  const sortBy = router.query.sortBy as string;
  const sortOrder = router.query.sortOrder as SortOrder;
  const addToBatch = router.query.addToBatch as string;
  const studentName = router.query.studentName as string;
  const page = Number(router.query.page || 1);

  const { toast } = useAscContext();
  const [members, setMembers] = useState<AnalyticsGroupMemberTableItem[]>([]);
  const [assignedStudents, setAssignedStudents] = useState<
    AnalyticsGroupMemberTableItem[]
  >([]);

  const [isSendEmailModalOpen, setSendEmailModalOpen] =
    useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  async function handleRemoveMember() {
    if (selectedStudentId) {
      await onRemove(selectedStudentId);
      getMembers(groupId);
    }
  }

  async function handleSave() {
    try {
      setLoading(true);
      router.push(SHARED_ADMIN_ROUTES.singleAnalyticsGroup(groupId as string));
      await onSave(assignedStudents.map((el) => el.id)).then(() => {
        getMembers(groupId);
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  async function getMembers(
    groupId: string,
    filters?: {
      sortBy?: string;
      sortDirection?: string;
      take?: number;
      skip?: number;
      searchTerm?: string;
    }
  ) {
    try {
      setLoading(true);
      const members = await onGetEventMembers(groupId, {
        ...filters,
        ...(filters?.sortBy
          ? { sortBy: filters?.sortBy }
          : { sortBy: "groupNames" }),
      });
      const groupMembers = members.filter(
        (member) => member?.groupIds.filter((el) => el === groupId).length > 0
      );
      setAssignedStudents(groupMembers);
      if (addToBatch) {
        setMembers(members);
      } else {
        setMembers(groupMembers);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (groupId && addToBatch) {
      getMembers(groupId, {
        sortBy,
        sortDirection: sortOrder,
        take: DEFAULT_PER_PAGE,
        skip: page - 1,
        searchTerm,
      });
    }
  }, [sortOrder, sortBy, page, addToBatch]);

  useEffect(() => {
    if (groupId) {
      getMembers(groupId);
    }
  }, [groupId]);

  useEffect(() => {
    if (!addToBatch) {
      sortFilterBatchStudentList();
    }
    setSearchTerm((studentName as string) || "");
  }, [studentName, sortBy, sortOrder]);

  function handleSearch() {
    if (groupId && addToBatch) {
      getMembers(groupId, {
        sortBy,
        sortDirection: sortOrder,
        take: DEFAULT_PER_PAGE,
        skip: page - 1,
        searchTerm,
      });
    } else {
      sortFilterBatchStudentList();
    }
    router.replace({
      query: { ...router.query, studentName: searchTerm, page: 1 },
    });
  }

  const handleRedirectAddToBatch = () => {
    router.replace({
      pathname: SHARED_ADMIN_ROUTES.singleAnalyticsGroup(groupId),
      query: { addToBatch: true, page: 1 },
    });
  };

  const sortFilterBatchStudentList = useCallback(() => {
    let batchStudents = assignedStudents || [];
    if (sortBy && sortOrder) {
      batchStudents = [...batchStudents].sort((a, b) => {
        const key = sortBy as unknown as keyof typeof a;
        const firstEl = String(a[key]).toLowerCase();
        const secondEl = String(b[key]).toLowerCase();
        return sortOrder === "desc" && firstEl > secondEl ? 1 : -1;
      });
    }
    if (searchTerm) {
      batchStudents = batchStudents.filter((el) =>
        el.name?.includes(searchTerm)
      );
    }

    setMembers(batchStudents);
  }, [searchTerm, sortBy, sortOrder]);

  return (
    <div
      data-testid="asc-analytics-group-members-list-table"
      className={classNames(styles.ground, className)}
    >
      <div className={styles.searchBlock}>
        <div className={styles.rightField}>
          <AscSearchInput
            value={searchTerm}
            placeholder={translate("searchByName") + "..."}
            onChange={(event) => setSearchTerm(event.target.value)}
            className={styles.input}
          />
          <AscButton className={styles.button} onClick={handleSearch}>
            {translate("search")}
          </AscButton>
        </div>

        <div className={styles.leftField}>
          {addToBatch ? (
            <AscButton className={styles.addButton} onClick={handleSave}>
              {translate("save")}
            </AscButton>
          ) : (
            <AscButton
              className={styles.addButton}
              onClick={handleRedirectAddToBatch}
            >
              {translate("addToBatch")}
            </AscButton>
          )}
        </div>
      </div>

      <AscLoading loading={loading} type="spinner" className={styles.spinner}>
        <AscTableResponsive<AnalyticsGroupMemberTableItem>
          title={translate("studentList")}
          rowKey="id"
          columns={[
            {
              dataIndex: "name",
              title: translate("name"),
              render: (el) => <>{el}</>,
              sort: {
                exist: true,
              },
              width: 220,
            },
            {
              dataIndex: "groupNames",
              title: translate("batch"),
              render: (batches: any) => (
                <>
                  {batches && Array.isArray(batches) && batches?.length > 0
                    ? batches?.map((el) => el).join(",")
                    : translate("notAssigned")}
                </>
              ),
              sort: {
                exist: true,
              },
              width: 250,
            },
            {
              dataIndex: "email",
              title: translate("email"),
              render: (el) => (
                <span
                  onDoubleClick={() => {
                    navigator.clipboard.writeText(el as string);
                    toast.info("Email has been copied to clipboard");
                  }}
                  className={styles.email}
                >
                  {el as string}
                </span>
              ),
              sort: {
                exist: true,
              },
              style: {
                display: "flex",
                flex: "1 1 0",
                width: "100%",
              },
              width: `calc(100% - 730px)`,
            },
            {
              dataIndex: "status",
              title: translate("status"),
              render: (el) => (
                <AscTag
                  label={el as string}
                  type={getStudentStatusTagType(el as string)}
                />
              ),
              sort: {
                exist: true,
              },
              align: "center",
              width: 150,
              style: { display: "flex", justifyContent: "center" },
            },
          ]}
          actions={(student) => [
            {
              title: translate("remove"),
              onClick: () => {
                setSelectedStudentId(student?.id);
                setDeleteModal(true);
              },
            },
            {
              title: translate("sendEmail"),
              onClick: () => {
                setSelectedStudentId(student?.id);
                setSendEmailModalOpen(true);
              },
            },
          ]}
          data={members}
          rowSelection={{
            onChange: (keys) => setAssignedStudents(keys),
            selectedRows: assignedStudents,
            hidden: !addToBatch,
          }}
          totalPage={
            (addToBatch ? members : assignedStudents).length >= DEFAULT_PER_PAGE
              ? page + 1
              : 1
          }
          showPerPage={false}
          emptyCard={
            !addToBatch &&
            assignedStudents.length < 1 && (
              <div className={styles.emptyCaseContainer}>
                <div className={styles.emptyCase}>
                  <InfoCircleSolid className={styles.emptyCaseIcon} />
                  <span className={styles.emptyCaseLabel}>
                    {translate("emptyBatchLabel")}
                  </span>
                </div>
                <AscButton
                  className={styles.assignTeacherBlockBtn}
                  onClick={handleRedirectAddToBatch}
                >
                  {translate("addToBatch")}
                </AscButton>
              </div>
            )
          }
        />
      </AscLoading>
      {isSendEmailModalOpen && selectedStudentId && (
        <AscBatchSendEmail
          visible={isSendEmailModalOpen}
          setVisible={setSendEmailModalOpen}
          sendMessage={onSendEmail}
          studentOptions={members}
          selectedStudentId={selectedStudentId}
        />
      )}
      {deleteModal && selectedStudentId && (
        <AscModalDelete
          visible={deleteModal}
          setVisible={setDeleteModal}
          onCancel={() => setSelectedStudentId("")}
          onApprove={() => handleRemoveMember()}
        />
      )}
    </div>
  );
}
